import {Container, Stack, Typography} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';
import {Placement} from 'react-joyride';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AlertActions,
	AlertModal,
	CTAButtonFixed,
	ErrorState,
	MainFooter,
} from 'presentation/components';
import Tutorial from 'presentation/components/Tutorial';
import {useMediaQueries} from 'presentation/pages/private/hooks';

import {useTreatmentDataStore} from '../../../Request/store/useTreatmentDataStore';
import {useRefundDocumentsStore} from '../../store/useRefundDocumentsStore';
import {SendDocumentDrawer} from './components/SendDocumentDrawer';
import {SendDocumentsCards} from './components/SendDocumentsCards';
import {SendDocumentsTable} from './components/SendDocumentsTable';
import {UploadTermsAcceptanceModal} from './components/UploadTermsAcceptanceModal';
import {
	ACTIONS,
	FIRST_ACTION_TARGET_ID,
	LOAD_ERROR_STATE,
	MISSING_REQUIRED_DOCS_CONFIRM,
	REPLACE_DOCUMENT_CONFIRM,
	TUTORIAL,
	UPLOAD_TERMS,
	getDocumentRowsByModality,
} from './constants';
import {StyledFooterContainer} from './styles';
import {RefundDocumentRow, RefundDocumentStatusEnum, SendDocumentsV2Props} from './types';

export function SendDocuments({handleNextStep}: SendDocumentsV2Props) {
	const {isMobile} = useMediaQueries();
	const treatmentData = useTreatmentDataStore(s => s.data);
	const documentRows = useRefundDocumentsStore(s => s.rows);
	const storedModalityKey = useRefundDocumentsStore(s => s.modalityKey);
	const seedRows = useRefundDocumentsStore(s => s.seedRows);
	const updateRow = useRefundDocumentsStore(s => s.updateRow);
	const [drawerRow, setDrawerRow] = useState<RefundDocumentRow | null>(null);
	const [replaceConfirmRow, setReplaceConfirmRow] = useState<RefundDocumentRow | null>(null);
	const [isMissingDocsConfirmOpen, setIsMissingDocsConfirmOpen] = useState(false);
	const [pendingTermsRow, setPendingTermsRow] = useState<RefundDocumentRow | null>(null);
	const [hasAcceptedUploadTerms, setHasAcceptedUploadTerms] = useState(
		() => typeof window !== 'undefined' && localStorage.getItem(UPLOAD_TERMS.storageKey) === 'true',
	);
	// TODO: derivar de uma query real quando o endpoint de listar documentos existir.
	const [hasLoadError, setHasLoadError] = useState(false);

	useEffect(() => {
		const nextKey = treatmentData?.modalityCode ?? 'none';
		if (storedModalityKey === nextKey && documentRows.length > 0) return;
		seedRows(getDocumentRowsByModality(treatmentData?.modalityCode), nextKey);
	}, [treatmentData?.modalityCode, storedModalityKey, documentRows.length, seedRows]);

	const handleRetryLoad = () => {
		setHasLoadError(false);
		// TODO: chamar refetch da query quando integrado ao endpoint real.
	};

	const hasMissingRequiredDocs = documentRows.some(
		row => row.obrigatorio && row.status === RefundDocumentStatusEnum.PENDENTE,
	);

	const handleProceed = () => {
		if (hasMissingRequiredDocs) {
			setIsMissingDocsConfirmOpen(true);
			return;
		}
		handleNextStep();
	};

	const handleConfirmProceed = () => {
		setIsMissingDocsConfirmOpen(false);
		handleNextStep();
	};

	const handleCancelProceed = () => {
		setIsMissingDocsConfirmOpen(false);
	};

	const openUploadFor = (row: RefundDocumentRow) => {
		if (row.status !== RefundDocumentStatusEnum.PENDENTE) {
			setReplaceConfirmRow(row);
			return;
		}
		setDrawerRow(row);
	};

	const handleSendDocument = (row: RefundDocumentRow) => {
		if (!hasAcceptedUploadTerms) {
			setPendingTermsRow(row);
			return;
		}
		openUploadFor(row);
	};

	const handleAcceptUploadTerms = () => {
		if (typeof window !== 'undefined') {
			localStorage.setItem(UPLOAD_TERMS.storageKey, 'true');
		}
		setHasAcceptedUploadTerms(true);
		const next = pendingTermsRow;
		setPendingTermsRow(null);
		if (next) openUploadFor(next);
	};

	const handleCancelUploadTerms = () => {
		setPendingTermsRow(null);
	};

	const handleConfirmReplace = () => {
		setDrawerRow(replaceConfirmRow);
		setReplaceConfirmRow(null);
	};

	const handleCancelReplace = () => {
		setReplaceConfirmRow(null);
	};

	const handleCloseDrawer = () => {
		setDrawerRow(null);
	};

	const handleSaveDocument = (file: File, row: RefundDocumentRow) => {
		// TODO: enviar arquivo para endpoint real e usar status retornado.
		// Mock: alterna entre Aprovado e Reprovado pra validar a UI do passo de revisão.
		const mockStatus =
			Math.random() < 0.3
				? RefundDocumentStatusEnum.REJEITADO
				: RefundDocumentStatusEnum.APROVADO;
		const today = new Date().toLocaleDateString('pt-BR');
		// TODO: substituir o objectURL local pela URL real retornada pelo endpoint de upload.
		const anexoUrl = URL.createObjectURL(file);
		updateRow(row.id, {
			anexo: file.name,
			dataEnvio: today,
			status: mockStatus,
			anexoUrl,
		});
		setDrawerRow(null);
	};

	const handleOpenAttachment = (row: RefundDocumentRow) => {
		if (row.anexoUrl) window.open(row.anexoUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<Fragment>
			<Container>
				<Stack mt={4} spacing={2}>
					{hasLoadError ? (
						<ErrorState
							title={LOAD_ERROR_STATE.title}
							description={LOAD_ERROR_STATE.description}
							handleRefresh={handleRetryLoad}
						/>
					) : (
						<>
							{isMobile ? (
								<SendDocumentsCards
									rows={documentRows}
									onSendDocument={handleSendDocument}
									onOpenAttachment={handleOpenAttachment}
								/>
							) : (
								<SendDocumentsTable
									rows={documentRows}
									onSendDocument={handleSendDocument}
									onOpenAttachment={handleOpenAttachment}
								/>
							)}

							<CTAButtonFixed.Root
								direction={!isMobile ? 'row' : 'column'}
								justifyContent="flex-end">
								<CTAButtonFixed.Action
									data-tag-id={
										RefundTaggingEnum.CLICK_PROSSEGUIR_PARA_REVISAO_REEMBOLSO
									}
									size="large"
									onClick={handleProceed}>
									{ACTIONS.proceedForConference}
								</CTAButtonFixed.Action>
							</CTAButtonFixed.Root>
						</>
					)}
				</Stack>
			</Container>

			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>

			<SendDocumentDrawer
				open={drawerRow !== null}
				row={drawerRow}
				onClose={handleCloseDrawer}
				onSave={handleSaveDocument}
			/>

			<AlertModal
				open={replaceConfirmRow !== null}
				onClose={handleCancelReplace}
				headerTitle={REPLACE_DOCUMENT_CONFIRM.headerTitle}
				variant="info"
				customIconName="info-filled"
				title={REPLACE_DOCUMENT_CONFIRM.title}
				description={REPLACE_DOCUMENT_CONFIRM.description}
				footerContent={
					<AlertActions
						primaryAction={handleConfirmReplace}
						secondaryAction={handleCancelReplace}
						primaryText={REPLACE_DOCUMENT_CONFIRM.confirm}
						secondaryText={REPLACE_DOCUMENT_CONFIRM.cancel}
						flexDirection="column-reverse"
					/>
				}
			/>

			<AlertModal
				open={isMissingDocsConfirmOpen}
				onClose={handleCancelProceed}
				headerTitle={MISSING_REQUIRED_DOCS_CONFIRM.headerTitle}
				variant="info"
				customIconName="info-filled"
				title={MISSING_REQUIRED_DOCS_CONFIRM.title}
				description={MISSING_REQUIRED_DOCS_CONFIRM.description}
				footerContent={
					<AlertActions
						primaryAction={handleConfirmProceed}
						secondaryAction={handleCancelProceed}
						primaryText={MISSING_REQUIRED_DOCS_CONFIRM.confirm}
						secondaryText={MISSING_REQUIRED_DOCS_CONFIRM.cancel}
						flexDirection="column-reverse"
					/>
				}
			/>

			<UploadTermsAcceptanceModal
				open={pendingTermsRow !== null}
				onClose={handleCancelUploadTerms}
				onConfirm={handleAcceptUploadTerms}
			/>

			{!hasLoadError ? (
				<Tutorial
					storageKey={TUTORIAL.storageKey}
					customButtonsLabels={{last: TUTORIAL.button}}
					steps={[
						{
							target: `#${FIRST_ACTION_TARGET_ID}`,
							disableBeacon: true,
							placement: 'top' as Placement,
							content: (
								<Stack gap={1}>
									<Typography variant="subtitle1" fontWeight={600}>
										{TUTORIAL.title}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{TUTORIAL.description}
									</Typography>
								</Stack>
							),
						},
					]}
				/>
			) : null}
		</Fragment>
	);
}
